import React, { useEffect, useState } from "react";
import {
  FaArrowLeft,
  FaArrowRight,
} from "react-icons/fa";

import Reveal from "../../../components/ui/Reveal";

import {
  JUDO_PRESENCE_GALLERY,
} from "../data/judoPresenceProData";

import { cn } from "../utils/judoPresenceProUtils";

export default function JudoPresenceProGallery() {
  const [activeIndex, setActiveIndex] = useState(0);

  const activeImage =
    JUDO_PRESENCE_GALLERY[activeIndex];

  const showPrevious = () => {
    setActiveIndex((currentIndex) =>
      currentIndex === 0
        ? JUDO_PRESENCE_GALLERY.length - 1
        : currentIndex - 1,
    );
  };

  const showNext = () => {
    setActiveIndex(
      (currentIndex) =>
        (currentIndex + 1) %
        JUDO_PRESENCE_GALLERY.length,
    );
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "ArrowLeft") {
        showPrevious();
      }

      if (event.key === "ArrowRight") {
        showNext();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <section className="overflow-hidden bg-[#f3f6f5] py-20 dark:bg-slate-900 sm:py-24 lg:py-28">
      <div className="mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="grid gap-8 border-b border-slate-300/70 pb-10 dark:border-white/10 lg:grid-cols-[1fr_0.8fr] lg:items-end">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#0868f2] dark:text-blue-300">
                Aperçu de l’application
              </p>

              <h2 className="mt-5 text-4xl font-semibold leading-[1.08] tracking-[-0.04em] text-slate-950 dark:text-white sm:text-5xl lg:text-6xl">
                Une interface claire,
                <span className="block text-[#0868f2] dark:text-blue-300">
                  pensée pour le terrain.
                </span>
              </h2>
            </div>

            <p className="max-w-xl text-base leading-8 text-slate-600 dark:text-slate-300 lg:justify-self-end">
              Parcourez les principales étapes, de la création du club
              jusqu’au suivi statistique des présences.
            </p>
          </div>
        </Reveal>

        <Reveal className="mt-12">
          <div className="overflow-hidden rounded-[26px] border border-slate-200 bg-white shadow-[0_30px_100px_-60px_rgba(15,23,42,0.55)] dark:border-white/10 dark:bg-slate-950">
            <div className="flex min-h-[420px] items-center justify-center bg-slate-100 p-4 dark:bg-slate-900 sm:p-7 lg:min-h-[620px]">
              <img
                key={activeImage.src}
                src={activeImage.src}
                alt={activeImage.alt}
                className="max-h-[680px] w-full object-contain"
              />
            </div>

            <div className="grid gap-6 border-t border-slate-200 px-5 py-6 dark:border-white/10 sm:px-7 lg:grid-cols-[1fr_auto] lg:items-center">
              <div>
                <div className="flex items-center gap-3">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#ff7900]">
                    {activeImage.category}
                  </span>

                  <span className="text-xs text-slate-400">
                    {String(activeIndex + 1).padStart(2, "0")}
                    {" / "}
                    {String(
                      JUDO_PRESENCE_GALLERY.length,
                    ).padStart(2, "0")}
                  </span>
                </div>

                <h3 className="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">
                  {activeImage.title}
                </h3>

                <p className="mt-2 max-w-3xl text-sm leading-7 text-slate-500 dark:text-slate-400">
                  {activeImage.caption}
                </p>
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={showPrevious}
                  className="grid h-11 w-11 place-items-center rounded-full border border-slate-200 text-slate-700 transition hover:border-[#0868f2] hover:bg-[#0868f2] hover:text-white dark:border-white/10 dark:text-white"
                  aria-label="Afficher l’image précédente"
                >
                  <FaArrowLeft className="text-xs" />
                </button>

                <button
                  type="button"
                  onClick={showNext}
                  className="grid h-11 w-11 place-items-center rounded-full bg-[#0868f2] text-white transition hover:bg-[#0648b8]"
                  aria-label="Afficher l’image suivante"
                >
                  <FaArrowRight className="text-xs" />
                </button>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal className="mt-6">
          <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
            <div className="flex min-w-max gap-3 pb-2">
              {JUDO_PRESENCE_GALLERY.map((image, index) => {
                const active = index === activeIndex;

                return (
                  <button
                    key={image.src}
                    type="button"
                    onClick={() => setActiveIndex(index)}
                    className={cn(
                      "group w-40 text-left sm:w-48",
                      "transition-opacity",
                      !active && "opacity-60 hover:opacity-100",
                    )}
                  >
                    <div
                      className={cn(
                        "aspect-[16/10] overflow-hidden rounded-[14px] border-2 bg-white dark:bg-slate-950",
                        active
                          ? "border-[#ff7900]"
                          : "border-transparent",
                      )}
                    >
                      <img
                        src={image.src}
                        alt=""
                        loading="lazy"
                        className="h-full w-full object-cover"
                      />
                    </div>

                    <p
                      className={cn(
                        "mt-2 truncate text-xs font-semibold",
                        active
                          ? "text-[#0868f2] dark:text-blue-300"
                          : "text-slate-500 dark:text-slate-400",
                      )}
                    >
                      {image.title}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}